import { Injectable } from '@angular/core';
import {
  DashboardBodyDto,
  DashboardRevenueProjectionBodyDto,
  DashboardGetAgentsResponse,
  DashboardGetAgentPortalPaidUploadHistoryResponse,
  DashboardGetListBodyDto,
  DashboardGetRevenueAnalyticsResponse,
  DashboardGetRevenueProjectionBonusResponse,
  DashboardGetRevenueProjectionChartResponse,
  DashboardGetSalesByCarriersResponse,
  DashboardGetSummaryResponse,
  DashboardGetTransactionsResponse,
  DashboardRevenueProjectionMonthResponse,
  DashboardTransactionsItemResponse,
  DashboardViewDto,
  DashboardListViewDto,
  DashboardTeamGetListBodyDto,
  DashboardTeamTransactionsBodyDto,
  DashboardHierarchyScopeDto,
  DashboardGetTeamRosterResponse,
  DashboardGetTeamLeadsResponse,
  ImportAgentPortalPaidFileResponse,
} from 'ag-common-lib/lib/contracts/reporting-api';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ReportingApiService } from './reporting-api.service';
import { PolicyTransactionKeys } from 'ag-common-lib/lib/models/domain/policy-transaction.model';

export interface DashboardStatsPayload {
  year: number;
  view: DashboardViewDto;
  startDate?: string;
  endDate?: string;
  productType?: string[];
}

export interface DashboardLegacyStats {
  year: number;
  monthStats: Array<{
    label: string;
    premiumTotal: number;
  }>;
  countTotal: number;
  totalWeightedPrem: number;
  avgWeightedPrem: number;
}

export interface DashboardLegacySalesByCarrier {
  name: string | null;
  premiumTotal: number;
}

type DashboardProjectionMonth = DashboardRevenueProjectionMonthResponse;

const EMPTY_PROJECTION_MONTH: DashboardProjectionMonth = {
  month: 0,
  actual: null,
  projected: null,
  low: null,
  high: null,
};

/**
 * Projection shaped for the chart: arrays indexed by month slot (0 = Jan), so they line up
 * with `DashboardLegacyStats.monthStats` without the component doing any lookups.
 * A `null` slot means "no projected value for this month" — i.e. it is settled actual.
 */
export interface DashboardProjectionStats {
  year: number;
  hasProjection: boolean;
  /** last settled week the projection was computed from (after the reporting-lag cutoff) */
  asOfWeek: number;
  /** last week the latest paid file covers — actuals run to here, the projection starts after */
  dataWeek: number;
  projected: Array<number | null>;
  low: Array<number | null>;
  high: Array<number | null>;
  projectedAnnual: number;
  projectedLow: number;
  projectedHigh: number;
}

export const DashboardTransactionKeys = {
  agentName: 'agentName',
  carrierName: 'carrierName',
  insuredName: 'insuredName',
  mgaName: 'mgaName',
  policies: 'policyCount',
  policyNumber: 'policyNumber',
  productName: 'productName',
  productType: 'productType',
  transDate: 'transactionDate',
  weightedPrem: 'weightedPremium',
} as const;

export const DashboardAgentKeys = {
  agentEmail: 'agentEmail',
  agentId: 'agentId',
  agentName: 'agentName',
  mgaId: 'mgaId',
  mgaName: 'mgaName',
  policies: 'policyCount',
  weightedPrem: 'weightedPremium',
} as const;

/**
 * Row identity for every agents summary grid.
 *
 * The API aggregates `group by (agent_id, mga_id)`, so an agent who produced under more than one
 * MGA legitimately comes back as several rows — 926 agents in production do, one across 24 MGAs.
 * Keying those rows on agentId alone declares an identity the data does not have: DevExtreme then
 * holds several different rows under one key in its virtual-scroll buffer, which surfaces as the
 * same agent appearing twice with different totals and rows landing out of sort order.
 *
 * The key has to be the same tuple the server grouped by.
 */
export const DASHBOARD_AGENTS_GRID_KEY = [DashboardAgentKeys.agentId, DashboardAgentKeys.mgaId];

type DxFilter = unknown[];

export interface DashboardTransactionsDataSourcePayload extends Omit<DashboardStatsPayload, 'view'> {
  /**
   * Wider than DashboardStatsPayload's: the grid endpoints implement 'team', while the
   * aggregate endpoints (summary, revenue-analytics, sales-by-carriers, projections) do
   * not — so the wider union stops here rather than widening the shared payload.
   */
  view: DashboardListViewDto;
  /** Viewpoint for `view: 'team'`; ignored by every other view. Server-validated. */
  rootAgentId?: string;
  filter?: unknown[];
  onSummary?: (summary: number[]) => void;
  searchValue?: string | null;
  sort?: unknown;
}

/**
 * Inputs for the hierarchy transactions endpoint.
 *
 * Deliberately has no `view`: the membership is resolved server-side from `scope` +
 * `rootAgentId` — the same pair the roster is asked for — so the grid and its drill-down
 * cannot describe different sets. Ids are never sent for scoping.
 */
export interface DashboardHierarchyTransactionsDataSourcePayload extends Omit<DashboardStatsPayload, 'view'> {
  scope: DashboardHierarchyScopeDto;
  /** Viewpoint. Absent means the flat membership, matching the roster's list shape. */
  rootAgentId?: string;
  /** Narrowing from a tree filter — the still-visible ids. Intersected server-side. */
  agentIds?: string[];
  filter?: unknown[];
}

export interface DashboardAgentsDataSourcePayload extends DashboardStatsPayload {
  filter?: unknown[];
  onSummary?: (summary: number[]) => void;
  searchValue?: string | null;
  sort?: unknown;
}

/** Years with agent-portal-paid data and the latest as-of date for each, from one request. */
export interface DashboardPaidFileYearSummary {
  /** descending */
  years: number[];
  asOfDateByYear: Record<number, string>;
}

export interface DashboardImportAgentPortalPaidFilePayload {
  year: number;
  asOfDate: string;
  file: File;
}

@Injectable({ providedIn: 'root' })
export class DashboardReportingApiService {
  constructor(private reportingApiService: ReportingApiService) {}

  getSummary = (body: DashboardBodyDto): Promise<DashboardGetSummaryResponse> => {
    return this.reportingApiService.request<DashboardGetSummaryResponse>('/api/dashboard/summary', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  getRevenueAnalytics = (body: DashboardBodyDto): Promise<DashboardGetRevenueAnalyticsResponse> => {
    return this.reportingApiService.request<DashboardGetRevenueAnalyticsResponse>('/api/dashboard/revenue-analytics', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  /** Per-carrier year-end projection + 7.5% bonus estimate (settled data only). */
  getRevenueProjectionBonus = (
    body: DashboardRevenueProjectionBodyDto,
  ): Promise<DashboardGetRevenueProjectionBonusResponse> => {
    return this.reportingApiService.request<DashboardGetRevenueProjectionBonusResponse>(
      '/api/dashboard/revenue-projection/bonus',
      { method: 'POST', body, responseType: 'json' },
    );
  };

  /** Monthly actual/projected series with a low-high range, for the Revenue Analytics chart. */
  getRevenueProjectionChart = (body: DashboardBodyDto): Promise<DashboardGetRevenueProjectionChartResponse> => {
    return this.reportingApiService.request<DashboardGetRevenueProjectionChartResponse>(
      '/api/dashboard/revenue-projection/chart',
      { method: 'POST', body, responseType: 'json' },
    );
  };

  /** Chart-ready projection for a year: 12 slots aligned with monthStats, plus the annual range. */
  getProjectionStats = async ({ year, view }: DashboardStatsPayload): Promise<DashboardProjectionStats> => {
    const response = await this.getRevenueProjectionChart(this.getYearBody(year, view));
    const data = response.data;
    const bySlot = (pick: (m: DashboardProjectionMonth) => number | null): Array<number | null> =>
      Array.from({ length: 12 }, (_, i) => pick(data.months.find(m => m.month === i + 1) ?? EMPTY_PROJECTION_MONTH));

    return {
      year,
      hasProjection: data.hasProjection,
      asOfWeek: data.asOfWeek,
      dataWeek: data.dataWeek,
      projected: bySlot(m => m.projected),
      low: bySlot(m => m.low),
      high: bySlot(m => m.high),
      projectedAnnual: data.projectedAnnual,
      projectedLow: data.projectedLow,
      projectedHigh: data.projectedHigh,
    };
  };

  getSalesByCarriers = (body: DashboardBodyDto): Promise<DashboardGetSalesByCarriersResponse> => {
    return this.reportingApiService.request<DashboardGetSalesByCarriersResponse>('/api/dashboard/sales-by-carriers', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  getAgents = (body: DashboardGetListBodyDto): Promise<DashboardGetAgentsResponse> => {
    return this.reportingApiService.request<DashboardGetAgentsResponse>('/api/dashboard/agents/get-list', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  getTransactions = (body: DashboardGetListBodyDto): Promise<DashboardGetTransactionsResponse> => {
    return this.reportingApiService.request<DashboardGetTransactionsResponse>('/api/dashboard/transactions/get-list', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  // Single-request "load-all" endpoints: return the fully-materialized grouped
  // tree (all levels, all leaf rows) in one call. See docs/dashboard-grid-load-all.md.
  getAgentsAll = (body: DashboardGetListBodyDto): Promise<DashboardGetAgentsResponse> => {
    return this.reportingApiService.request<DashboardGetAgentsResponse>('/api/dashboard/agents/get-list/all', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  getTransactionsAll = (body: DashboardGetListBodyDto): Promise<DashboardGetTransactionsResponse> => {
    return this.reportingApiService.request<DashboardGetTransactionsResponse>(
      '/api/dashboard/transactions/get-list/all',
      {
        method: 'POST',
        body,
        responseType: 'json',
      },
    );
  };

  /**
   * The team hierarchy for a viewpoint, as flat rows the TreeList assembles via
   * `agentId` / `managerAgentId`.
   *
   * Always the whole subtree: DevExtreme TreeList forces `remoteOperations.paging`
   * off, so there is no page to ask for. Filtering and search stay client-side —
   * server-side filtering would strip ancestors and orphan the matched nodes.
   */
  /**
   * Transactions for exactly the agents `getTeamRoster` renders for the same inputs.
   *
   * A separate endpoint rather than a client-side filter on the shared one: the filter
   * could only ever narrow what is displayed, while the scope here is what the caller is
   * permitted to read. Passing the same `scope` + `rootAgentId` as the roster is what
   * makes the two agree.
   */
  getTeamTransactions = (body: DashboardTeamTransactionsBodyDto): Promise<DashboardGetTransactionsResponse> => {
    return this.reportingApiService.request<DashboardGetTransactionsResponse>(
      '/api/dashboard/team/transactions/get-list',
      { method: 'POST', body, responseType: 'json' },
    );
  };

  /** Load-all twin of `getTeamTransactions`, for the export path. Same scope resolution. */
  getTeamTransactionsAll = (body: DashboardTeamTransactionsBodyDto): Promise<DashboardGetTransactionsResponse> => {
    return this.reportingApiService.request<DashboardGetTransactionsResponse>(
      '/api/dashboard/team/transactions/get-list/all',
      { method: 'POST', body, responseType: 'json' },
    );
  };

  getTeamRoster = (body: DashboardTeamGetListBodyDto): Promise<DashboardGetTeamRosterResponse> => {
    return this.reportingApiService.request<DashboardGetTeamRosterResponse>('/api/dashboard/team/get-list', {
      method: 'POST',
      body,
      responseType: 'json',
    });
  };

  /**
   * Selectable viewpoints, drawn from the same membership the roster resolves for this
   * `scope`. Independent of the current root, so re-rooting cannot shrink the control
   * that does the re-rooting.
   */
  getTeamLeads = (scope?: DashboardHierarchyScopeDto): Promise<DashboardGetTeamLeadsResponse> => {
    return this.reportingApiService.request<DashboardGetTeamLeadsResponse>('/api/dashboard/team/leads', {
      method: 'POST',
      body: { scope },
      responseType: 'json',
    });
  };

  // Flatten a (possibly multi-level grouped) get-list/all response down to its
  // leaf data rows. Used by the export path so a single load-all request feeds
  // the whole spreadsheet (no paged chunk loop / N+1).
  flattenGridTreeLeaves<TLeaf>(nodes: readonly unknown[] | undefined): TLeaf[] {
    const leaves: TLeaf[] = [];
    const visit = (items: readonly unknown[] | undefined): void => {
      if (!Array.isArray(items)) {
        return;
      }
      for (const item of items) {
        const groupItems = (item as { items?: unknown[] } | null)?.items;
        if (Array.isArray(groupItems)) {
          visit(groupItems);
        } else {
          leaves.push(item as TLeaf);
        }
      }
    };
    visit(nodes);
    return leaves;
  }
  importAgentPortalPaidFile = (
    payload: DashboardImportAgentPortalPaidFilePayload,
  ): Promise<ImportAgentPortalPaidFileResponse> => {
    const formData = new FormData();
    formData.append('year', String(payload.year));
    formData.append('asOfDate', payload.asOfDate);
    formData.append('file', payload.file, payload.file.name);

    return this.reportingApiService.request<ImportAgentPortalPaidFileResponse>(
      '/api/dashboard/import-agent-portal-paid-file',
      {
        method: 'POST',
        body: formData,
        responseType: 'json',
      },
    );
  };

  getAgentPortalPaidUploadHistory = (
    body: Pick<DashboardGetListBodyDto, 'loadOptions'>,
  ): Promise<DashboardGetAgentPortalPaidUploadHistoryResponse> => {
    return this.reportingApiService.request<DashboardGetAgentPortalPaidUploadHistoryResponse>(
      '/api/dashboard/import-agent-portal-paid-file/get-list',
      {
        method: 'POST',
        body,
        responseType: 'json',
      },
    );
  };

  /**
   * As-of date of the most recent import, ordered by upload time rather than by as-of date.
   * An import replaces its year wholesale, so only the newest upload describes what the tables
   * actually hold; the highest as-of date ever submitted may belong to a superseded attempt.
   */
  getLatestPaidFileAsOfDate = async (): Promise<string | null> => {
    const response = await this.getAgentPortalPaidUploadHistory({
      loadOptions: {
        sort: [
          { selector: 'createdAt', desc: true },
          { selector: 'id', desc: true },
        ],
        skip: 0,
        take: 1,
      },
    } as Pick<DashboardGetListBodyDto, 'loadOptions'>);
    const asOfDate = (response?.data?.[0] as { asOfDate?: unknown } | undefined)?.asOfDate;

    return typeof asOfDate === 'string' && asOfDate.trim() ? asOfDate : null;
  };

  /**
   * Years for the picker and the header's as-of date, from a route that asks for nothing
   * else.
   *
   * This used to reduce the upload-history list in the browser: a thousand rows fetched
   * to keep two columns, behind a permission that grants the whole import history. An
   * agent without the reports permissions got a 403, the error was swallowed, and their
   * year picker rendered empty with no explanation.
   */
  getPaidFileYearSummary = async (): Promise<DashboardPaidFileYearSummary> => {
    return this.reportingApiService.request<DashboardPaidFileYearSummary>('/api/dashboard/paid-file-years', {
      method: 'GET',
      responseType: 'json',
    });
  };

  getAgentPortalPaidUploadHistoryDataSource = (): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: 'id',
        load: loadOptions => {
          return this.getAgentPortalPaidUploadHistory({
            loadOptions: this.normalizeLoadOptions(loadOptions as Record<string, unknown>),
          });
        },
      }),
    });
  };

  downloadAgentPortalPaidFile = (id: number): Promise<Blob> => {
    return this.reportingApiService.request<Blob>(`/api/dashboard/import-agent-portal-paid-file/${id}/download`, {
      method: 'GET',
      responseType: 'blob',
    });
  };

  exportAgents = (body: DashboardGetListBodyDto): Promise<Blob> => {
    return this.reportingApiService.request<Blob>('/api/dashboard/agents/export', {
      method: 'POST',
      body: {
        ...body,
        loadOptions: this.normalizeAgentsLoadOptions((body.loadOptions ?? {}) as Record<string, unknown>),
      },
      responseType: 'blob',
    });
  };

  exportTransactions = (body: DashboardGetListBodyDto): Promise<Blob> => {
    return this.reportingApiService.request<Blob>('/api/dashboard/transactions/export', {
      method: 'POST',
      body: {
        ...body,
        loadOptions: this.normalizeTransactionsLoadOptions((body.loadOptions ?? {}) as Record<string, unknown>),
      },
      responseType: 'blob',
    });
  };

  getStats = async ({ year, view }: DashboardStatsPayload): Promise<DashboardLegacyStats> => {
    const body = this.getYearBody(year, view);
    const [summaryResponse, revenueAnalyticsResponse] = await Promise.all([
      this.getSummary(body),
      this.getRevenueAnalytics(body),
    ]);
    const summary = summaryResponse.data;

    return {
      year,
      monthStats: revenueAnalyticsResponse.data.items.map(item => ({
        label: this.getMonthLabel(item.month),
        premiumTotal: item.totalPremium,
      })),
      countTotal: summary.totalPoliciesSold,
      totalWeightedPrem: summary.totalWeightedPremium,
      avgWeightedPrem: summary.averageWeightedPremium,
    };
  };

  /**
   * Current + prior year stats for Revenue Analytics / agent cards.
   *
   * One `/summary` (selected year) + one `/revenue-analytics` spanning both calendar
   * years — previously each year called getStats and doubled both endpoints.
   */
  getYearOverYearStats = async ({
    year,
    view,
  }: DashboardStatsPayload): Promise<{
    currentYearStats: DashboardLegacyStats;
    lastYearStats: DashboardLegacyStats;
  }> => {
    const lastYear = year - 1;
    const currentYearBody = this.getYearBody(year, view);
    const [summaryResponse, revenueAnalyticsResponse] = await Promise.all([
      this.getSummary(currentYearBody),
      this.getRevenueAnalytics({
        ...currentYearBody,
        startDate: `${lastYear}-01-01`, // from Jan 1 of last year through the selected year end, then splits months client-side
      }),
    ]);
    const summary = summaryResponse.data;
    const { currentYearMonthStats, lastYearMonthStats } = this.splitRevenueAnalyticsByYear(
      revenueAnalyticsResponse.data.items,
      year,
      lastYear,
    );
    const lastYearTotalWeightedPrem = lastYearMonthStats.reduce((total, month) => total + (month.premiumTotal ?? 0), 0);

    return {
      currentYearStats: {
        year,
        monthStats: currentYearMonthStats,
        countTotal: summary.totalPoliciesSold,
        totalWeightedPrem: summary.totalWeightedPremium,
        avgWeightedPrem: summary.averageWeightedPremium,
      },
      lastYearStats: {
        year: lastYear,
        monthStats: lastYearMonthStats,
        countTotal: 0,
        totalWeightedPrem: lastYearTotalWeightedPrem,
        avgWeightedPrem: 0,
      },
    };
  };

  getSalesByCarrier = async ({ year, view }: DashboardStatsPayload): Promise<DashboardLegacySalesByCarrier[]> => {
    const response = await this.getSalesByCarriers(this.getYearBody(year, view));

    return response.data.items.map(item => ({
      name: item.carrierName,
      premiumTotal: item.totalPremium,
    }));
  };

  /**
   * Production rolled up by period (year|quarter) per id — the reporting-API replacement for the
   * elastic getAgentProductionByYears / getAgencyProductionByYears. The grid does single-level
   * grouping, so we issue one grouped transactions query per id: group transactionDate via
   * groupInterval and sum policies + premium. Returns Map<id, { [periodKey]: { premiumTotal, countTotal } }>.
   */
  getProductionByYears = async (
    ids: string[],
    kind: 'agent' | 'agency' = 'agent',
    interval: 'year' | 'quarter' = 'year',
  ): Promise<Map<string, Record<string, { premiumTotal: number; countTotal: number }>>> => {
    const filterField = kind === 'agency' ? 'mgaId' : 'agentId';
    const map = new Map<string, Record<string, { premiumTotal: number; countTotal: number }>>();

    await Promise.all(
      (ids ?? []).filter(Boolean).map(async id => {
        map.set(id, await this.loadProductionRollup(interval, [filterField, '=', id]));
      }),
    );

    return map;
  };

  /** Period bucket keys that have data — replaces the elastic getReportColumns. */
  getProductionColumns = async (interval: 'year' | 'quarter' = 'year'): Promise<string[]> => {
    return Object.keys(await this.loadProductionRollup(interval));
  };

  /**
   * Legacy monthly production stats for one agent/mga — replaces the elastic getStats. Builds the
   * 12-month premium series + year totals from the transactions grid (month buckets via
   * groupInterval + totalSummary), filtered by agentId/mgaId when supplied.
   */
  getProductionStats = async (payload: {
    year: number;
    agentId?: string;
    mgaId?: string;
  }): Promise<DashboardLegacyStats> => {
    const year = payload?.year ?? new Date().getFullYear();
    // Scope through `view`, not only through the grid filter. The API skips its PORTAL_ADMIN check
    // solely for the scoped views, so asking for 'all' makes every non-admin agent get a 403 for
    // their own production. 'personal'/'agency' narrow server-side by the authenticated (or
    // impersonated) agent; the grid filter below stays as a client-side guard.
    const view: DashboardViewDto = payload?.agentId ? 'personal' : payload?.mgaId ? 'agency' : 'all';
    const range = { startDate: `${year}-01-01`, endDate: `${year}-12-31`, view };
    const filter = payload?.agentId
      ? ['agentId', '=', payload.agentId]
      : payload?.mgaId
        ? ['mgaId', '=', payload.mgaId]
        : undefined;

    const [monthlyResponse, totalsResponse] = await Promise.all([
      this.getTransactions({
        ...range,
        loadOptions: {
          ...(filter ? { filter } : {}),
          group: [{ selector: 'transactionDate', groupInterval: 'month' }],
          groupSummary: [{ selector: 'weightedPremium', summaryType: 'sum' }],
          requireGroupCount: true,
          skip: 0,
          take: 1000,
        },
      } as DashboardGetListBodyDto) as Promise<{ data?: { key?: unknown; summary?: number[] }[] }>,
      this.getTransactions({
        ...range,
        loadOptions: {
          ...(filter ? { filter } : {}),
          requireTotalCount: true,
          totalSummary: [
            { selector: 'policyCount', summaryType: 'sum' },
            { selector: 'weightedPremium', summaryType: 'sum' },
          ],
          skip: 0,
          take: 0,
        },
      } as DashboardGetListBodyDto) as Promise<{ summary?: number[] }>,
    ]);

    const premiumByMonth = new Map<number, number>();
    for (const group of monthlyResponse?.data ?? []) {
      const monthDate = new Date(String(group?.key ?? ''));
      if (!Number.isNaN(monthDate.getTime())) {
        premiumByMonth.set(monthDate.getUTCMonth(), Number(group?.summary?.[0] ?? 0));
      }
    }
    const monthStats = Array.from({ length: 12 }, (_, month) => ({
      label: new Date(Date.UTC(year, month, 1)).toLocaleString('en', { month: 'short' }),
      premiumTotal: premiumByMonth.get(month) ?? 0,
    }));

    const totals = totalsResponse?.summary ?? [];
    const countTotal = Number(totals[0] ?? 0);
    const totalWeightedPrem = Number(totals[1] ?? 0);

    return {
      year,
      monthStats,
      countTotal,
      totalWeightedPrem,
      avgWeightedPrem: countTotal ? totalWeightedPrem / countTotal : totalWeightedPrem,
    };
  };

  /**
   * Distinct years that have uploaded data (desc). Delegates to the shared summary so the year
   * picker and the header's as-of date come from one request instead of two identical ones.
   */
  getAvailableYears = async (): Promise<number[]> => {
    return (await this.getPaidFileYearSummary()).years;
  };

  /**
   * Per-agent production summaries for an MGA in a year — replaces getFromElastic in
   * agency-hierarchy. Returns Map<agentId, summary> keyed by the PolicyTransactionKeys the
   * hierarchy calc reads (agent_id / policies / weighted_prem).
   */
  getAgentSummariesByMga = async (
    mgaId: string,
    year: number,
  ): Promise<Map<string, { agent_id: string; policies: number; weighted_prem: number }>> => {
    // The 'agency' view scopes to the *effective* agent's own MGA and rejects outright
    // when that agent has none — `Agency view requires MGA id`. Under impersonation the
    // effective agent is the impersonation target, so an admin viewing someone without
    // an MGA would otherwise turn a missing id into a 403. There is no agency to report
    // on in that case, so answer with an empty map rather than asking.
    if (!mgaId) {
      return new Map();
    }

    const response = (await this.getAgentsAll({
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
      // 'agency' scopes server-side to the caller's own MGA. 'all' is the admin view
      // and 403s every non-PORTAL_ADMIN caller, which is what left the hierarchy tree
      // stuck on its loading panel for the managers this screen exists for.
      view: 'agency',
      // Still bounded by MGA on the request: for a PORTAL_ADMIN the server scope is
      // unrestricted, and without this an admin would pull every agent on the platform
      // into the browser on each render.
      loadOptions: { filter: ['mgaId', '=', mgaId] },
    } as DashboardGetListBodyDto)) as { data?: unknown[] };

    const map = new Map<string, { agent_id: string; policies: number; weighted_prem: number }>();
    for (const agent of this.flattenGridTreeLeaves<Record<string, unknown>>(response?.data)) {
      const agentId = agent?.['agentId'] as string;
      if (!agentId) {
        continue;
      }
      map.set(agentId, {
        agent_id: agentId,
        policies: Number(agent?.['policyCount'] ?? 0),
        weighted_prem: Number(agent?.['weightedPremium'] ?? 0),
      });
    }
    return map;
  };

  private loadProductionRollup = async (
    interval: 'year' | 'quarter',
    filter?: unknown[],
  ): Promise<Record<string, { premiumTotal: number; countTotal: number }>> => {
    const currentYear = new Date().getFullYear();
    const response = (await this.getTransactions({
      startDate: `${currentYear - 20}-01-01`,
      endDate: `${currentYear}-12-31`,
      view: 'all',
      loadOptions: {
        ...(filter ? { filter } : {}),
        group: [{ selector: 'transactionDate', groupInterval: interval }],
        groupSummary: [
          { selector: 'policyCount', summaryType: 'sum' },
          { selector: 'weightedPremium', summaryType: 'sum' },
        ],
        requireGroupCount: true,
        skip: 0,
        take: 1000,
      },
    } as DashboardGetListBodyDto)) as { data?: { key?: unknown; summary?: number[] }[] };

    const stats: Record<string, { premiumTotal: number; countTotal: number }> = {};
    for (const group of response?.data ?? []) {
      const raw = String(group?.key ?? '');
      if (!raw) {
        continue;
      }
      // The group key is a date_trunc bucket serialized as a UTC instant
      // ("2025-01-01T00:00:00.000Z"); read the period in UTC so the year/quarter
      // is not shifted by the viewer's timezone. Falls back to the raw key if unparseable.
      const key = this.formatPeriodKey(raw, interval);
      const summary = group?.summary ?? [];
      stats[key] = { countTotal: Number(summary[0] ?? 0), premiumTotal: Number(summary[1] ?? 0) };
    }
    return stats;
  };

  /** Reduce a date_trunc group-key instant to a timezone-safe period label (UTC). */
  private formatPeriodKey = (raw: string, interval: 'year' | 'quarter'): string => {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    const year = date.getUTCFullYear();
    if (interval === 'quarter') {
      return `${year}-Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
    }
    return String(year);
  };

  getTransactionsDataSource = ({
    year,
    view,
    startDate,
    endDate,
    filter,
    rootAgentId,
  }: DashboardTransactionsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: 'id',
        load: loadOptions => {
          const gridLoadOptions = loadOptions as Record<string, unknown>;
          // AND the selected-agents filter (if any) with the grid's own filters.
          const mergedFilter = this.combineFilters(gridLoadOptions.filter, filter);
          const normalizedLoadOptions = this.normalizeLoadOptions({ ...gridLoadOptions, filter: mergedFilter });

          return this.getTransactions({
            ...this.getYearBody(year, view, { startDate, endDate }),
            rootAgentId,
            loadOptions: normalizedLoadOptions,
          }).then(response => this.normalizeGroupResponse(response, normalizedLoadOptions));
        },
      }),
    });
  };

  /**
   * Transactions restricted to the agents the hierarchy above the modal renders.
   *
   * `scope` + `rootAgentId` go to the server, which resolves the membership itself — the
   * request stays the same size whether the tree has three rows or three thousand, and a
   * stripped body cannot widen it. `agentIds` is only a further narrowing.
   */
  getHierarchyTransactionsDataSource = ({
    year,
    scope,
    rootAgentId,
    agentIds,
    startDate,
    endDate,
    filter,
  }: DashboardHierarchyTransactionsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: 'id',
        load: loadOptions => {
          const gridLoadOptions = loadOptions as Record<string, unknown>;
          const mergedFilter = this.combineFilters(gridLoadOptions.filter, filter);
          const normalizedLoadOptions = this.normalizeLoadOptions({ ...gridLoadOptions, filter: mergedFilter });
          const { view, ...dateRange } = this.getYearBody(year, 'team', { startDate, endDate });

          return this.getTeamTransactions({
            ...dateRange,
            scope,
            rootAgentId,
            agentIds,
            loadOptions: normalizedLoadOptions,
          }).then(response => this.normalizeGroupResponse(response, normalizedLoadOptions));
        },
      }),
    });
  };

  private combineFilters(gridFilter: unknown, baseFilter: unknown): unknown {
    const hasGrid = Array.isArray(gridFilter) && gridFilter.length > 0;
    const hasBase = Array.isArray(baseFilter) && baseFilter.length > 0;

    if (hasGrid && hasBase) {
      return [gridFilter, 'and', baseFilter];
    }

    return hasGrid ? gridFilter : hasBase ? baseFilter : gridFilter;
  }

  getAgentsDataSource = ({ year, view, startDate, endDate, productType }: DashboardStatsPayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: DASHBOARD_AGENTS_GRID_KEY,
        load: loadOptions => {
          const normalizedLoadOptions = this.normalizeAgentsLoadOptions(loadOptions as Record<string, unknown>);

          return this.getAgents({
            ...this.getYearBody(year, view, { startDate, endDate }),
            ...(productType?.length ? { productType } : {}),
            loadOptions: normalizedLoadOptions,
          }).then(response => this.normalizeGroupResponse(response, normalizedLoadOptions));
        },
      }),
    });
  };

  getPwaTransactionsListDataSource = ({
    year,
    view,
    filter,
    onSummary,
    searchValue,
    sort,
  }: DashboardTransactionsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: 'id',
        load: async loadOptions => {
          const response = await this.getTransactions({
            ...this.getYearBody(year, view),
            loadOptions: this.normalizeTransactionsLoadOptions({
              ...(loadOptions as Record<string, unknown>),
              filter,
              searchValue,
              sort: sort ?? (loadOptions as Record<string, unknown>).sort,
              totalSummary: [
                { selector: DashboardTransactionKeys.weightedPrem, summaryType: 'sum' },
                { selector: DashboardTransactionKeys.policies, summaryType: 'sum' },
              ],
            }),
          });

          if ('summary' in response) {
            onSummary?.(response.summary ?? []);
          }

          return {
            ...response,
            data: response.data.map(transaction => ({
              id: (transaction as DashboardTransactionsItemResponse).id,
              policy: this.toLegacyPolicyTransaction(transaction as DashboardTransactionsItemResponse),
              total: (transaction as DashboardTransactionsItemResponse).policyCount ?? 0,
            })),
          };
        },
      }),
    });
  };

  getPwaPolicyTransactionsDataSource = ({
    year,
    view,
    filter,
    searchValue,
    sort,
  }: DashboardTransactionsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 20,
      store: new CustomStore({
        key: 'id',
        load: async loadOptions => {
          const response = await this.getTransactions({
            ...this.getYearBody(year, view),
            loadOptions: this.normalizeTransactionsLoadOptions({
              ...(loadOptions as Record<string, unknown>),
              filter,
              searchValue,
              sort: sort ?? (loadOptions as Record<string, unknown>).sort,
            }),
          });

          return {
            ...response,
            data: response.data.map(transaction =>
              this.toLegacyPolicyTransaction(transaction as DashboardTransactionsItemResponse),
            ),
          };
        },
      }),
    });
  };

  getPwaAgentsSummaryDataSource = ({
    year,
    view,
    filter,
    onSummary,
    searchValue,
    sort,
  }: DashboardAgentsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 15,
      store: new CustomStore({
        key: DASHBOARD_AGENTS_GRID_KEY,
        load: async loadOptions => {
          const normalizedLoadOptions = this.normalizeAgentsLoadOptions({
            ...(loadOptions as Record<string, unknown>),
            filter,
            searchValue,
            sort: sort ?? (loadOptions as Record<string, unknown>).sort,
            totalSummary: [
              { selector: DashboardAgentKeys.weightedPrem, summaryType: 'sum' },
              { selector: DashboardAgentKeys.policies, summaryType: 'sum' },
            ],
          });
          const response = await this.getAgents({
            ...this.getYearBody(year, view),
            loadOptions: normalizedLoadOptions,
          });

          if ('summary' in response) {
            onSummary?.(response.summary ?? []);
          }

          return {
            ...response,
            data: response.data.map(item => this.toLegacyAgentSummary(item)),
          };
        },
      }),
    });
  };

  getPwaAgenciesSummaryDataSource = ({
    year,
    view,
    filter,
    onSummary,
    sort,
  }: DashboardAgentsDataSourcePayload): DataSource => {
    return new DataSource({
      paginate: true,
      requireTotalCount: true,
      pageSize: 15,
      store: new CustomStore({
        key: PolicyTransactionKeys.mgaId,
        load: async loadOptions => {
          const normalizedLoadOptions = this.normalizeAgentsLoadOptions({
            filter,
            sort: sort ?? (loadOptions as Record<string, unknown>).sort,
            take: 10000,
            userData: { isLoadingAll: true },
          });
          const response = await this.getAgents({
            ...this.getYearBody(year, view),
            loadOptions: normalizedLoadOptions,
          });
          const agencySummaries = this.getAgencySummaries(response.data);
          const sortedAgencySummaries = this.sortAgencySummaries(agencySummaries, sort);
          const skip = Number((loadOptions as Record<string, unknown>).skip ?? 0);
          const take = Number((loadOptions as Record<string, unknown>).take ?? sortedAgencySummaries.length);
          const page = sortedAgencySummaries.slice(skip, skip + take);
          const summary = [
            agencySummaries.reduce((total, item) => total + Number(item[PolicyTransactionKeys.weightedPrem] ?? 0), 0),
            agencySummaries.reduce((total, item) => total + Number(item[PolicyTransactionKeys.policies] ?? 0), 0),
          ];

          onSummary?.(summary);

          return {
            data: page,
            totalCount: agencySummaries.length,
            summary,
          };
        },
      }),
    });
  };

  private getYearBody<TView extends DashboardListViewDto>(
    year: number,
    view: TView,
    dateRange?: Pick<DashboardStatsPayload, 'startDate' | 'endDate'>,
  ): Omit<DashboardBodyDto, 'view'> & { view: TView } {
    const selectedYear = year ?? new Date().getFullYear();

    return {
      startDate: dateRange?.startDate ?? `${selectedYear}-01-01`,
      endDate: dateRange?.endDate ?? `${selectedYear}-12-31`,
      view,
    };
  }

  private getMonthLabel(month: string): string {
    // `month` is a date-only bucket ('2026-06-01'), which new Date() parses as UTC midnight.
    // Render in UTC so a viewer behind UTC doesn't see the previous month.
    const date = new Date(month);

    if (Number.isNaN(date.getTime())) {
      return month;
    }

    return date.toLocaleString('en', { month: 'short', timeZone: 'UTC' });
  }

  private splitRevenueAnalyticsByYear(
    items: Array<{ month: string; totalPremium: number }>,
    currentYear: number,
    lastYear: number,
  ): {
    currentYearMonthStats: DashboardLegacyStats['monthStats'];
    lastYearMonthStats: DashboardLegacyStats['monthStats'];
  } {
    const currentYearMonthStats: DashboardLegacyStats['monthStats'] = [];
    const lastYearMonthStats: DashboardLegacyStats['monthStats'] = [];

    (items ?? []).forEach(item => {
      const monthDate = new Date(item.month);
      if (Number.isNaN(monthDate.getTime())) {
        return;
      }

      const itemYear = monthDate.getUTCFullYear();
      const monthStat = {
        label: this.getMonthLabel(item.month),
        premiumTotal: item.totalPremium,
      };

      if (itemYear === currentYear) {
        currentYearMonthStats.push(monthStat);
        return;
      }

      if (itemYear === lastYear) {
        lastYearMonthStats.push(monthStat);
      }
    });

    return { currentYearMonthStats, lastYearMonthStats };
  }

  private normalizeLoadOptions(loadOptions: Record<string, unknown>): Record<string, unknown> {
    return this.normalizeLoadOptionsSearchValues(loadOptions);
  }

  private normalizeAgentsLoadOptions(loadOptions: Record<string, unknown>): Record<string, unknown> {
    const normalizedLoadOptions = this.normalizeLoadOptionsSearchValues({
      ...loadOptions,
      filter: Array.isArray(loadOptions.filter) ? this.mapAgentFilterFields(loadOptions.filter) : loadOptions.filter,
      sort: Array.isArray(loadOptions.sort) ? this.mapAgentSortFields(loadOptions.sort) : loadOptions.sort,
    });
    const searchValue = normalizedLoadOptions.searchValue;

    if (typeof searchValue !== 'string' || !searchValue) {
      return normalizedLoadOptions;
    }

    const searchFilter = this.buildAgentSearchFilter(searchValue);
    const existingFilter = normalizedLoadOptions.filter;

    return {
      ...normalizedLoadOptions,
      searchValue: null,
      filter:
        Array.isArray(existingFilter) && existingFilter.length ? [existingFilter, 'and', searchFilter] : searchFilter,
    };
  }

  private normalizeTransactionsLoadOptions(loadOptions: Record<string, unknown>): Record<string, unknown> {
    const normalizedLoadOptions = this.normalizeLoadOptionsSearchValues({
      ...loadOptions,
      filter: Array.isArray(loadOptions.filter)
        ? this.mapTransactionFilterFields(loadOptions.filter)
        : loadOptions.filter,
      sort: Array.isArray(loadOptions.sort) ? this.mapTransactionSortFields(loadOptions.sort) : loadOptions.sort,
    });
    const searchValue = normalizedLoadOptions.searchValue;

    if (typeof searchValue !== 'string' || !searchValue) {
      return normalizedLoadOptions;
    }

    const searchFilter = this.buildTransactionSearchFilter(searchValue);
    const existingFilter = normalizedLoadOptions.filter;

    return {
      ...normalizedLoadOptions,
      searchValue: null,
      filter:
        Array.isArray(existingFilter) && existingFilter.length ? [existingFilter, 'and', searchFilter] : searchFilter,
    };
  }

  private normalizeLoadOptionsSearchValues(loadOptions: Record<string, unknown>): Record<string, unknown> {
    const searchValue = loadOptions?.searchValue;
    const normalizedLoadOptions = {
      ...loadOptions,
      filter: Array.isArray(loadOptions.filter)
        ? this.normalizeFilterSearchValues(loadOptions.filter)
        : loadOptions.filter,
    };

    if (typeof searchValue !== 'string') {
      return normalizedLoadOptions;
    }

    const normalizedSearchValue = this.normalizeNumericSearchValue(searchValue);

    if (normalizedSearchValue === searchValue) {
      return normalizedLoadOptions;
    }

    return {
      ...normalizedLoadOptions,
      searchValue: normalizedSearchValue,
    };
  }

  private normalizeFilterSearchValues(filter: DxFilter): DxFilter {
    if (this.isSimpleFilter(filter)) {
      const [field, operation, value] = filter;
      return [field, operation, typeof value === 'string' ? this.normalizeNumericSearchValue(value) : value];
    }

    return filter.map(item => (Array.isArray(item) ? this.normalizeFilterSearchValues(item) : item));
  }

  private normalizeGroupResponse<T extends { data?: unknown[]; totalCount?: number; groupCount?: number }>(
    response: T,
    loadOptions: Record<string, unknown>,
  ): T {
    const group = loadOptions.group;
    const requireGroupCount = loadOptions.requireGroupCount === true;

    if (!requireGroupCount || !Array.isArray(group) || response?.groupCount !== undefined) {
      return response;
    }

    return {
      ...response,
      groupCount: response?.totalCount ?? response?.data?.length ?? 0,
    };
  }

  private buildAgentSearchFilter(searchValue: string): DxFilter {
    const stringValue = this.normalizeNumericSearchValue(searchValue);
    const searchFilter: DxFilter = [
      [DashboardAgentKeys.agentName, 'contains', stringValue],
      'or',
      [DashboardAgentKeys.agentEmail, 'contains', stringValue],
      'or',
      [DashboardAgentKeys.agentId, 'contains', stringValue],
      'or',
      [DashboardAgentKeys.mgaId, 'contains', stringValue],
      'or',
      [DashboardAgentKeys.mgaName, 'contains', stringValue],
    ];
    const numericValue = this.getNumericSearchValue(stringValue);

    if (numericValue !== null) {
      searchFilter.push('or', [DashboardAgentKeys.policies, '=', numericValue], 'or', [
        DashboardAgentKeys.weightedPrem,
        '=',
        numericValue,
      ]);
    }

    return searchFilter;
  }

  private mapTransactionFilterFields(filter: DxFilter): DxFilter {
    if (this.isSimpleFilter(filter)) {
      const [field, operation, value] = filter;
      return [this.getTransactionApiField(field), operation, value];
    }

    return filter.map(item => (Array.isArray(item) ? this.mapTransactionFilterFields(item) : item));
  }

  private mapAgentFilterFields(filter: DxFilter): DxFilter {
    if (this.isSimpleFilter(filter)) {
      const [field, operation, value] = filter;
      return [this.getAgentApiField(field), operation, value];
    }

    return filter.map(item => (Array.isArray(item) ? this.mapAgentFilterFields(item) : item));
  }

  private mapTransactionSortFields(sort: unknown[]): unknown[] {
    return sort.map(item => {
      if (!item || typeof item !== 'object' || !('selector' in item)) {
        return item;
      }

      return {
        ...item,
        selector: this.getTransactionApiField((item as { selector: unknown }).selector),
      };
    });
  }

  private mapAgentSortFields(sort: unknown[]): unknown[] {
    return sort.map(item => {
      if (!item || typeof item !== 'object' || !('selector' in item)) {
        return item;
      }

      return {
        ...item,
        selector: this.getAgentApiField((item as { selector: unknown }).selector),
      };
    });
  }

  private buildTransactionSearchFilter(searchValue: string): DxFilter {
    const normalizedValue = this.normalizeNumericSearchValue(searchValue);
    const searchFilter: DxFilter = [
      [DashboardTransactionKeys.agentName, 'contains', normalizedValue],
      'or',
      [DashboardTransactionKeys.mgaName, 'contains', normalizedValue],
      'or',
      [DashboardTransactionKeys.insuredName, 'contains', normalizedValue],
      'or',
      [DashboardTransactionKeys.carrierName, 'contains', normalizedValue],
      'or',
      [DashboardTransactionKeys.policyNumber, 'contains', normalizedValue],
      'or',
      [DashboardTransactionKeys.productName, 'contains', normalizedValue],
    ];
    const numericValue = this.getNumericSearchValue(normalizedValue);

    if (numericValue !== null) {
      searchFilter.push('or', [DashboardTransactionKeys.policies, '=', numericValue], 'or', [
        DashboardTransactionKeys.weightedPrem,
        '=',
        numericValue,
      ]);
    }

    return searchFilter;
  }

  private getTransactionApiField(field: unknown): unknown {
    const fieldMap: Record<string, string> = {
      [PolicyTransactionKeys.agentDisplayName]: DashboardTransactionKeys.agentName,
      [PolicyTransactionKeys.agentId]: 'agentId',
      [PolicyTransactionKeys.carrierName]: DashboardTransactionKeys.carrierName,
      [PolicyTransactionKeys.insuredName]: DashboardTransactionKeys.insuredName,
      [PolicyTransactionKeys.mgaId]: 'mgaId',
      [PolicyTransactionKeys.mgaName]: DashboardTransactionKeys.mgaName,
      [PolicyTransactionKeys.policies]: DashboardTransactionKeys.policies,
      [PolicyTransactionKeys.policyNumber]: DashboardTransactionKeys.policyNumber,
      [PolicyTransactionKeys.productName]: DashboardTransactionKeys.productName,
      [PolicyTransactionKeys.transDate]: DashboardTransactionKeys.transDate,
      [PolicyTransactionKeys.weightedPrem]: DashboardTransactionKeys.weightedPrem,
    };

    return typeof field === 'string' ? fieldMap[field] ?? field : field;
  }

  private getAgentApiField(field: unknown): unknown {
    const fieldMap: Record<string, string> = {
      [PolicyTransactionKeys.agentDisplayName]: DashboardAgentKeys.agentName,
      [PolicyTransactionKeys.agentEmail]: DashboardAgentKeys.agentEmail,
      [PolicyTransactionKeys.agentId]: DashboardAgentKeys.agentId,
      [PolicyTransactionKeys.agentName]: DashboardAgentKeys.agentName,
      [PolicyTransactionKeys.mgaId]: DashboardAgentKeys.mgaId,
      [PolicyTransactionKeys.mgaName]: DashboardAgentKeys.mgaName,
      [PolicyTransactionKeys.policies]: DashboardAgentKeys.policies,
      [PolicyTransactionKeys.weightedPrem]: DashboardAgentKeys.weightedPrem,
    };

    return typeof field === 'string' ? fieldMap[field] ?? field : field;
  }

  private toLegacyPolicyTransaction(transaction: DashboardTransactionsItemResponse): Record<string, unknown> {
    return {
      ...transaction,
      [PolicyTransactionKeys.agentId]: transaction.agentId,
      [PolicyTransactionKeys.agentDisplayName]: transaction.agentName,
      [PolicyTransactionKeys.carrierName]: transaction.carrierName,
      [PolicyTransactionKeys.insuredName]: transaction.insuredName,
      [PolicyTransactionKeys.mgaId]: transaction.mgaId,
      [PolicyTransactionKeys.mgaName]: transaction.mgaName,
      [PolicyTransactionKeys.policies]: transaction.policyCount,
      [PolicyTransactionKeys.policyNumber]: transaction.policyNumber,
      [PolicyTransactionKeys.productName]: transaction.productName,
      [PolicyTransactionKeys.transDate]: transaction.transactionDate,
      [PolicyTransactionKeys.weightedPrem]: transaction.weightedPremium,
    };
  }

  private toLegacyAgentSummary(agent: unknown): Record<string, unknown> {
    const item = agent as Record<string, unknown>;
    const agentName = item[DashboardAgentKeys.agentName];
    const agentEmail = item[DashboardAgentKeys.agentEmail];

    return {
      ...item,
      [PolicyTransactionKeys.agentDisplayName]: [agentName, agentEmail].filter(Boolean).join(' - '),
      [PolicyTransactionKeys.agentEmail]: agentEmail,
      [PolicyTransactionKeys.agentId]: item[DashboardAgentKeys.agentId],
      [PolicyTransactionKeys.agentName]: agentName,
      [PolicyTransactionKeys.mgaId]: item[DashboardAgentKeys.mgaId],
      [PolicyTransactionKeys.mgaName]: item[DashboardAgentKeys.mgaName],
      [PolicyTransactionKeys.policies]: item[DashboardAgentKeys.policies],
      [PolicyTransactionKeys.weightedPrem]: item[DashboardAgentKeys.weightedPrem],
    };
  }

  private getAgencySummaries(agents: unknown[]): Record<string, unknown>[] {
    const agenciesMap = new Map<string, Record<string, unknown>>();

    agents.forEach(agent => {
      const item = agent as Record<string, unknown>;
      const mgaId = item[DashboardAgentKeys.mgaId] as string;

      if (!mgaId) {
        return;
      }

      const current = agenciesMap.get(mgaId) ?? {
        [PolicyTransactionKeys.count]: 0,
        [PolicyTransactionKeys.mgaId]: mgaId,
        [PolicyTransactionKeys.mgaName]: item[DashboardAgentKeys.mgaName],
        [PolicyTransactionKeys.policies]: 0,
        [PolicyTransactionKeys.weightedPrem]: 0,
      };

      current[PolicyTransactionKeys.count] = Number(current[PolicyTransactionKeys.count] ?? 0) + 1;
      current[PolicyTransactionKeys.policies] =
        Number(current[PolicyTransactionKeys.policies] ?? 0) + Number(item[DashboardAgentKeys.policies] ?? 0);
      current[PolicyTransactionKeys.weightedPrem] =
        Number(current[PolicyTransactionKeys.weightedPrem] ?? 0) + Number(item[DashboardAgentKeys.weightedPrem] ?? 0);

      agenciesMap.set(mgaId, current);
    });

    return Array.from(agenciesMap.values());
  }

  private sortAgencySummaries(agencySummaries: Record<string, unknown>[], sort: unknown): Record<string, unknown>[] {
    const sortDescriptor = Array.isArray(sort) ? sort[0] : null;

    if (!sortDescriptor || typeof sortDescriptor !== 'object' || !('selector' in sortDescriptor)) {
      return agencySummaries;
    }

    const selector = this.getTransactionApiField((sortDescriptor as { selector: unknown }).selector) as string;
    const legacySelector =
      selector === DashboardTransactionKeys.weightedPrem
        ? PolicyTransactionKeys.weightedPrem
        : selector === DashboardTransactionKeys.policies
          ? PolicyTransactionKeys.policies
          : selector;
    const direction = (sortDescriptor as { desc?: boolean }).desc ? -1 : 1;

    return [...agencySummaries].sort((left, right) => {
      const leftValue = left[legacySelector];
      const rightValue = right[legacySelector];

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }

      return String(leftValue ?? '').localeCompare(String(rightValue ?? '')) * direction;
    });
  }

  private isSimpleFilter(filter: DxFilter): filter is [string, string, unknown] {
    return filter.length === 3 && typeof filter[0] === 'string' && typeof filter[1] === 'string';
  }

  private normalizeNumericSearchValue(value: string): string {
    const trimmedValue = value.trim();
    const isDecimalNumber = /^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(trimmedValue);

    if (!isDecimalNumber) {
      return value;
    }

    return trimmedValue.replace(',', '.');
  }



  private getNumericSearchValue(value: string): number | null {
    const normalizedValue = this.normalizeNumericSearchValue(value);

    if (normalizedValue === value && normalizedValue.trim() !== value.trim().replace(',', '.')) {
      return null;
    }

    const numberValue = Number(normalizedValue);
    return Number.isFinite(numberValue) ? numberValue : null;
  }
}
